import { orderBy } from 'lodash';
import { DossierId } from '@/dossier/models/AbstractDossier';
import Job from '@/jobs/models/Job';
import MediaTargetType from '@/shared/constants/MediaTargetType';
import AbstractContext from '@/shared/models/AbstractContext';
import TimeService from '../services/TimeService';
import MediaFile from './MediaFile';
import MediaTarget, { DossierMediaTarget, JobMediaTarget } from './MediaTarget';

export default abstract class MediaContext extends AbstractContext {
  placeholders: Array<MediaFile> = [];

  constructor(
    readonly target: MediaTargetType,
    readonly dossierId: DossierId,
    readonly list: Array<MediaFile> = [],
    readonly file?: MediaFile,
    readonly editable = true,
    readonly canAdd = true,
  ) {
    super();
    this.sortList();
  }

  sortList(): void {
    this.list.splice(0, this.list.length, ...orderBy(this.list, 'createdOn', 'desc'));
  }

  abstract visit<Result>(visitor: MediaContextVisitor<Result>): Result;

  abstract withData(data: Partial<MediaContext>): MediaContext;

  abstract asTarget(): MediaTarget;

  addPlaceholder(data: Partial<MediaFile>): MediaFile {
    const placeholder = MediaFile.withData({
      createdOn: TimeService.now.value,
      ...data,
    });
    this.placeholders.push(placeholder);
    this.list.push(placeholder);
    return placeholder;
  }
}

export interface MediaContextVisitor<Result> {
  dossier(context: DossierMediaContext): Result;

  job(context: JobMediaContext): Result;
}

export class DossierMediaContext extends MediaContext {
  constructor(
    dossierId: DossierId,
    list?: Array<MediaFile>,
    file?: MediaFile,
  ) {
    super(MediaTargetType.DOSSIER, dossierId, list, file);
  }

  visit<Result>(visitor: MediaContextVisitor<Result>): Result {
    return visitor.dossier(this);
  }

  withData(data: Partial<MediaContext>): MediaContext {
    return Object.assign(new DossierMediaContext(this.dossierId, this.list, this.file), data);
  }

  asTarget(): DossierMediaTarget {
    return { type: MediaTargetType.DOSSIER, id: this.dossierId };
  }
}

export class JobMediaContext extends MediaContext {
  constructor(
    readonly job: Job,
    dossierId: DossierId,
    file?: MediaFile,
    canAdd?: boolean,
  ) {
    super(MediaTargetType.JOB, dossierId, job.mediaFiles, file, true, canAdd);
  }

  visit<Result>(visitor: MediaContextVisitor<Result>): Result {
    return visitor.job(this);
  }

  withData(data: Partial<MediaContext>): JobMediaContext {
    return Object.assign(new JobMediaContext(this.job, this.dossierId, this.file, this.canAdd), data);
  }

  asTarget(): JobMediaTarget {
    return { type: MediaTargetType.JOB, id: this.job.id };
  }
}